import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import React, {useState} from "react";
import {SxProps, Theme} from "@mui/material";
import {SelectValue} from "../../../types";
import theme, {FONTS} from "../../../constants/theme";
import {scale} from "../../../utils";

interface Props {
  variant: 'standard' | 'filled' | 'outlined';
  label: string;
  id: string;
  values: Array<SelectValue>;
  selectedValue?: SelectValue;
  setSelectedValue: (value: SelectValue) => void;
  sxProp?: SxProps<Theme>
}

const inputLabelStyle = {
  color: theme.palette.ceramic.contrastText,
  fontFamily: FONTS.MONTSERRAT,
  fontSize: scale(16),
  lineHeight: scale(22),
  fontWeight: 700,
  textTransform: 'uppercase',
  overflow: 'visible',
}

const MenuItemSx = (variant: 'standard' | 'filled' | 'outlined'): SxProps<Theme> => ({
  color: theme.palette.lightGray.contrastText,
  fontSize: variant === 'filled' ? scale(16) : 'unset',
  '&.Mui-selected': {
    fontWeight: 600,
    color: theme.palette.lightGray.contrastText,
  },
});

const ThemedSelect =  ({variant, label, id, values, selectedValue, setSelectedValue, sxProp}: Props) => {
  const [error, setError] = useState<boolean>(false);
  const [value, setValue] = useState<SelectValue | undefined>(selectedValue);

  const handleChange = (event: SelectChangeEvent) => {
    const newValue: SelectValue = {value: event.target.value, name: values.find(el => el.value === event.target.value)?.name ?? '-'}
    console.log('value', newValue);

    if (isError(newValue)) {
      setSelectedValue({value: '-', name: '-'})
      setError(true);
      return;
    }
    setError(false);
    setSelectedValue(newValue);
    setValue(newValue);
  };

  const isError = (newValue: SelectValue): boolean => {
    return (newValue.value === '-' || newValue.name === '-')
  }

  return (
    <div style={{gap: 0}}>
      {variant === 'filled' ? (
        <InputLabel
          htmlFor={label}
          sx={[inputLabelStyle, {color: error ? theme.palette.error.main : theme.palette.ceramic.contrastText}]}
        >
          {label}
        </InputLabel>) : null}
      <FormControl
        variant={variant}
        size={'small'}
        sx={{
          m: scale(1),
          minWidth: scale(400),
          ...sxProp,
          ...(variant === 'standard'
            ? {
              '& .MuiInput-underline:not(.Mui-error):before': {
                borderBottomColor: theme.palette.primary.main,
              },
              '& .MuiInput-underline:not(.Mui-error):hover:before': {
                borderBottomColor: theme.palette.primary.main,
              },
              '& .MuiInput-underline:not(.Mui-error):after': {
                borderBottomColor: theme.palette.primary.main,
              },
            }
            : {
              '& .MuiFilledInput-root': {
                borderRadius: 0,
              },
            }),
        }}
      >
        {variant === 'standard' ? (<InputLabel id={id.concat('-input-label')}>{label}</InputLabel>) : null}
        <Select
          labelId={id.concat('-select-standard-label')}
          id={id}
          value={value?.value ?? ''}
          onChange={handleChange}
          label={label}
          error={error}
          sx={{
            width: '100%',
            color: theme.palette.lightGray.contrastText,
            '& .MuiFilledInput-input': {
              paddingTop: 0,
              paddingBottom: 0,
              height: scale(40),
              fontSize: scale(16),
              lineHeight: scale(38)
            },
            ...(variant === 'filled'
              ? {
                '& .MuiFilledInput-input': {
                  paddingLeft: scale(8),
                  paddingTop: 0,
                  paddingBottom: 0,
                  height: scale(40),
                  fontSize: scale(16),
                  lineHeight: scale(38),
                },
              }
              : {

              }),
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: variant === 'filled' ? 0 : "unset",
                fontSize: variant === 'filled' ? scale(14) : "unset",
              },
            },
          }}
        >
          {values.map((el, index) => (
            <MenuItem key={index} value={el.value} sx={MenuItemSx(variant)}>{el.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}

export default ThemedSelect;
